import React, { useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Copy, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { generateReferralUrl } from '@/utils/referralUtils';
import { useAnalytics } from '@/hooks/useAnalytics';
import Header from '@/components/common/Header';
import FooterSection from '@/components/homepage/FooterSection';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

const ReferralPage: React.FC = () => {
  const { referralCode } = useParams<{ referralCode: string }>();
  const { toast } = useToast();
  const { trackButtonClick } = useAnalytics(); // This will automatically track page views
  const [copied, setCopied] = useState(false);

  if (!referralCode) {
    return <Navigate to="/" replace />;
  }

  const referralUrl = generateReferralUrl(referralCode);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      trackButtonClick('Copy Referral Link', 'referral_page');
      toast({
        title: "Copied!",
        description: "Referral link copied to clipboard",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({
        title: "Failed to copy",
        description: "Please try again",
        variant: "destructive",
      });
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: 'Join me on Wabisabi',
      text: 'I just joined the Wabisabi waitlist! Use my referral link to join, and we\'ll both move up the queue and earn €20 reward',
      url: referralUrl,
    };

    try {
      if (navigator.share && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        trackButtonClick('Share Referral Link', 'referral_page');
      } else {
        // Fallback for browsers that don't support sharing
        handleCopy();
      }
    } catch (err) {
      // User cancelled share or error occurred
      console.log('Share cancelled or failed');
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF5F3] flex flex-col">
      <Header />
      
      <main className="flex-grow px-4 py-12 mt-[100px]">
        {/* Hero Section */}
        <div className="flex flex-col items-center text-center mb-16">
          <h1 className="mb-8 text-4xl font-bold tracking-tight text-[#6C4F77] md:text-5xl lg:text-6xl max-w-4xl">
            Because love lives in the details
          </h1>
          <p className="mb-10 max-w-4xl text-[#2C2C2C] text-lg md:text-xl leading-relaxed" style={{ fontFamily: 'Euclid Circular A' }}>
            Wabisabi is an AI-powered Relationship Intelligence platform that helps you remember what matters — the little preferences, quiet moments, and check-ins that bring relationships to life.
          </p>
        </div>

        {/* Referral Content */}
        <div className="w-full max-w-3xl mx-auto bg-white rounded-lg p-8 md:p-12 shadow-sm">
          {/* Main Section */}
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-[#2C2C2C] mb-4">
              Share Wabisabi and earn €20 for every 5 friends
            </h1>
            <p className="text-lg text-[#666666] max-w-2xl mx-auto">
              Bring your inner circle to Wabisabi and earn €20 when 5 of them sign up using your code.
              It's that simple with no limit on rewards. Every friend you refer to gets the full Wabisabi
              experience and free access to the app for the first 3 months.
            </p>
          </div>

          {/* How it works Section */}
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-6">How it works:</h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-[#6C4F77] text-white rounded-full flex items-center justify-center font-semibold">
                  1
                </div>
                <div>
                  <h3 className="font-semibold text-[#2C2C2C] mb-1">Get Your Unique Code</h3>
                  <p className="text-[#666666]">Copy your personal referral code mentioned below or use the link</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-[#6C4F77] text-white rounded-full flex items-center justify-center font-semibold">
                  2
                </div>
                <div>
                  <h3 className="font-semibold text-[#2C2C2C] mb-1">Share with Friends</h3>
                  <p className="text-[#666666]">Send it to people who'll love being more thoughtful and connected.</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-[#6C4F77] text-white rounded-full flex items-center justify-center font-semibold">
                  3
                </div>
                <div>
                  <h3 className="font-semibold text-[#2C2C2C] mb-1">Earn Rewards</h3>
                  <p className="text-[#666666]">Once 5 friends sign up with your code, you receive your €20 reward.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Referral Code Section */}
          <div className="bg-[#FBF5F3] rounded-lg p-6 mb-12">
            <h2 className="text-xl font-semibold text-[#2C2C2C] mb-4">Your Referral Code</h2>
            <div className="bg-white rounded border border-[#DDDDDD] p-4 mb-4">
              <p className="text-sm text-[#666666] mb-1">Referral Link</p>
              <p className="font-mono text-[#2C2C2C] break-all">{referralUrl}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleCopy}
                variant="outline"
                className="w-full sm:w-auto"
              >
                <Copy className="w-4 h-4 mr-2" />
                {copied ? 'Copied!' : 'Copy Link'}
              </Button>
              <Button
                onClick={handleShare}
                className="w-full sm:w-auto bg-[#6C4F77] hover:bg-[#5A3A68]"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>
          </div>

          {/* FAQs Section */}
          <div>
            <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-6">FAQs</h2>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger className="text-left">
                  When do I get my €20 reward?
                </AccordionTrigger>
                <AccordionContent>
                  You'll be credited as soon as your 5th referred friend signs and we have verified all the signups using your code.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2">
                <AccordionTrigger className="text-left">
                  Is there a limit to how much I can earn?
                </AccordionTrigger>
                <AccordionContent>
                  No! Earn €20 for every set of 5 successful sign-ups.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-3">
                <AccordionTrigger className="text-left">
                  Can I share my code anywhere?
                </AccordionTrigger>
                <AccordionContent>
                  Absolutely. You can share it via social media, link, email, or text.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Terms & Conditions Link */}
          <div className="mt-8 text-center border-t pt-6">
            <p className="text-sm text-[#666666]">
              By participating in our referral program, you agree to our{' '}
              <Link to="/referral-terms" className="text-[#6C4F77] hover:underline">
                Terms & Conditions
              </Link>
            </p>
          </div>
        </div>
      </main>

      <FooterSection />
    </div>
  );
};

export default ReferralPage;